import { createFileRoute, Link } from "@tanstack/react-router";
import { TerminalModuleStrip } from "@/components/terminal/TerminalModuleStrip"
import {
  ArrowRight,
  Crosshair,
  Eye,
  ShieldCheck,
  Activity,
  AlertTriangle,
  Landmark,
  Target as TargetIcon,
  Droplets,
  ChevronRight,
  ChevronDown,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Layout } from "@/components/site/Layout";
import orionHero from "@/assets/orion-hero.webp";
import orionHero768 from "@/assets/orion-hero-768.webp";

const ORION_HERO_OG = "https://xauproacademy.com/orion-hero.png";

const ORION_FAQ = [
  {
    q: "What is ORION?",
    a: "Orion AI is XAU Pro Academy's proprietary gold decision engine suite. OTAS-01 and OSAS-01 provide live tactical and strategic coverage. OEAS-01 remains research only.",
  },
  {
    q: "Does ORION trade during news releases?",
    a: "Publication may be restricted under each live engine's own risk policy. Market news can create gaps, spreads and slippage, and no product control eliminates those risks.",
  },
  {
    q: "How are setups graded?",
    a: "Published opportunities carry a grade and engine attribution. A grade describes the platform's classification, not a guaranteed probability of success. Internal grading criteria remain proprietary.",
  },
  {
    q: "How much does each trade risk?",
    a: "Eligible members see each published trade's levels. Monetary risk depends on your execution and position size. Each engine has its own operating policy, and Orion AI does not enforce your account limits.",
  },
  {
    q: "Is ORION's performance audited?",
    a: "Every decision and outcome is logged. The public performance record and validation pages show closed results, including losses.",
  },
];

export const Route = createFileRoute("/orion")({
  head: () => ({
    meta: [
      { title: "AI Gold Analysis & SMC Member workflow for XAUUSD | Orion" },
      {
        name: "description",
        content:
          "Orion is the AI gold analysis and SMC decision engine behind XAU Pro Academy's published XAUUSD decisions. Fewer trades, cleaner reasoning.",
      },
      { property: "og:title", content: "AI Gold Analysis & SMC Member workflow for XAUUSD | Orion" },
      {
        property: "og:description",
        content:
          "Orion produces XAU Pro Academy's published XAUUSD decisions. Live tactical and strategic coverage, a separate research engine and member-facing trade updates.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://xauproacademy.com/orion" },
      { property: "og:image", content: ORION_HERO_OG },
      { property: "og:image:width", content: "1920" },
      { property: "og:image:height", content: "820" },
      { property: "og:image:alt", content: "ORION, the XAU Pro Academy AI engine, drawing a golden arrow at a XAUUSD chart" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: ORION_HERO_OG },
    ],
    links: [{ rel: "canonical", href: "https://xauproacademy.com/orion" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Meet ORION, The ORION Engine",
          url: "https://xauproacademy.com/orion",
          image: ORION_HERO_OG,
          description:
            "Orion AI is XAU Pro Academy's proprietary gold engine suite with live tactical and strategic coverage and a separate research model.",
          isPartOf: {
            "@type": "WebSite",
            name: "XAU Pro Academy",
            url: "https://xauproacademy.com",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: ORION_FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: OrionPage,
});

function OrionPage() {
  return (
    <Layout>
      <TerminalModuleStrip module="Orion AI" />
      {/* 1. Cinematic hero */}
      <section className="relative overflow-hidden bg-gradient-hero">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div
          className="pointer-events-none absolute -top-40 left-1/2 h-[32rem] w-[56rem] -translate-x-1/2 rounded-full opacity-40 blur-3xl"
          style={{ background: "radial-gradient(closest-side, hsl(var(--gold) / 0.45), transparent)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/30 to-background" />

        <div className="container relative mx-auto max-w-6xl px-4 pb-10 pt-12 md:pb-20 md:pt-24">
          <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-14">
            {/* Copy */}
            <div className="order-2 text-center lg:order-1 lg:text-left">
              <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold-muted px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
                <Crosshair className="h-3 w-3" /> Codename: ORION
              </div>
              <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight md:text-5xl lg:text-6xl">
                Meet ORION: The Engine Engineered for{" "}
                <span className="text-gradient-gold">Precision and Protection</span>.
              </h1>
              <p className="mt-6 font-display text-xl text-gradient-gold md:text-2xl">
                Systematic precision. Institutional discipline.
              </p>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg lg:mx-0 mx-auto">
                A proprietary gold engine suite for tactical and strategic decision support.
                Published trade plans, model attribution and status updates in one workspace.
              </p>
              <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center lg:justify-start">
                <Button asChild variant="gold" size="xl">
                  <Link to="/signals">
                    Open the Signals Desk <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to="/orion/how-it-works">How Orion thinks</Link>
                </Button>
              </div>
            </div>

            {/* Hero image */}
            <div className="order-1 lg:order-2">
              <div className="relative mx-auto max-w-xl lg:max-w-none">
                <div className="absolute -inset-1 rounded-2xl bg-gradient-gold opacity-25 blur-xl" aria-hidden />
                <div className="relative overflow-hidden rounded-2xl border border-gold/30 bg-slate-deep shadow-2xl">
                  <img
                    src={orionHero}
                    srcSet={`${orionHero768} 768w, ${orionHero} 1280w`}
                    sizes="(max-width: 1023px) 100vw, 560px"
                    alt="ORION, the XAU Pro Academy AI engine, drawing a golden arrow at a XAUUSD chart"
                    width={1920}
                    height={820}
                    className="h-auto w-full"
                    loading="eager"
                    fetchPriority="high"
                  />

                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
                  <div className="pointer-events-none absolute inset-0 grid-bg opacity-15" />
                  <div className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-md border border-gold/40 bg-background/70 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-gold backdrop-blur">
                    <Crosshair className="h-3 w-3" /> ORION / v1
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Introduction */}
      <section className="container mx-auto max-w-3xl px-4 py-12 md:py-24">
        <div className="space-y-6 text-base leading-relaxed text-muted-foreground md:text-lg">
          <p>
            XAUUSD rewards patience and punishes frequency. XAU Pro Academy built its
            decision engine, codenamed <span className="font-semibold text-foreground">ORION</span>,
            around that observation.
          </p>
          <p>
            ORION is not an automated bot chasing momentum. It is a rule based, multi layered
            suite with distinct model responsibilities and a recorded performance history. Its operating principle:
          </p>
          <blockquote className="rounded-xl border-l-2 border-gold bg-slate-deep/60 py-4 pl-6 pr-4 font-display text-lg italic text-foreground md:text-xl">
            A trader is defined not only by when they trade, but by when they refuse to.
          </blockquote>
          <p>
            Each live engine follows its own publication policy. No-trade periods are an acceptable output.
          </p>
        </div>
      </section>

      {/* Engine suite */}
      <section className="container mx-auto max-w-6xl px-4 pb-20 md:pb-24">
        <div className="text-center">
          <div className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
            Orion AI engine suite
          </div>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight md:text-4xl">
            Three engine roles. Two live models.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
            Tactical and strategic models provide live coverage. The research model remains separate, and each engine retains its own operating policy.
          </p>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {[
            {
              code: "OTAS-01",
              name: "Tactical Intraday",
              status: "Live",
              body: "Tactical gold opportunities for the active trading desk, with published trade plans and lifecycle updates.",
            },
            {
              code: "OSAS-01",
              name: "Strategic Swing",
              status: "Live",
              body: "Strategic gold opportunities with a broader holding horizon, displayed alongside tactical trades in the XAU Terminal™.",
            },
            {
              code: "OEAS-01",
              name: "Expansion Research",
              status: "Lab validation",
              body: "A separate research engine under evaluation. It does not provide live member signals, and lab results are not live performance.",
            },
          ].map((e) => (
            <div key={e.code} className="rounded-xl border border-gold/20 bg-card p-6">
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm font-bold text-gold">{e.code}</span>
                <span className="rounded-full border border-gold/25 bg-gold-muted px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-gold">
                  {e.status}
                </span>
              </div>
              <h3 className="mt-3 font-display text-xl font-semibold">{e.name}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{e.body}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-center text-xs text-muted-foreground">
          Engine parameters are proprietary and never published. Members see the resulting levels, not the rules.
        </p>
      </section>

      {/* 3. Product outputs: Technical Vigilance */}
      <section className="border-y border-border/60 bg-slate-deep">
        <div className="container mx-auto max-w-6xl px-4 py-20 md:py-24">
          <div className="grid gap-12 lg:grid-cols-[auto_1fr] lg:items-start lg:gap-16">
            <div className="flex justify-center lg:justify-start">
              <div className="relative">
                <div className="absolute -inset-3 rounded-2xl bg-gradient-gold opacity-20 blur-xl" aria-hidden />
                <div className="relative flex h-24 w-24 items-center justify-center rounded-2xl border border-gold/40 bg-gold-muted text-gold">
                  <Eye className="h-12 w-12" />
                </div>
              </div>
            </div>
            <div>
              <div className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
                Product outputs
              </div>
              <h2 className="mt-2 font-display text-3xl font-bold tracking-tight md:text-4xl">
                Published Plans, Not Strategy Blueprints
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
                Eligible members receive structured trade information. These outputs support independent decisions without exposing the underlying methods:
              </p>

              <div className="mt-8 grid gap-4 md:grid-cols-3">
                <FeatureCard
                  icon={<TargetIcon className="h-5 w-5" />}
                  title="Engine Attribution"
                  body="Identify the live model responsible for a published opportunity."
                />
                <FeatureCard
                  icon={<Crosshair className="h-5 w-5" />}
                  title="Published Trade Levels"
                  body="Review the published entry, invalidation and targets available for your membership."
                />
                <FeatureCard
                  icon={<Activity className="h-5 w-5" />}
                  title="Lifecycle Status"
                  body="Follow a published trade's updates and recorded outcome."
                />
              </div>

              <p className="mt-8 border-l-2 border-gold/60 pl-4 text-sm italic text-muted-foreground md:text-base">
                A published plan is decision support, not a guarantee or an instruction to execute.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Risk oversight: Macro Defense */}
      <section className="container mx-auto max-w-6xl px-4 py-20 md:py-24">
        <div className="grid gap-12 lg:grid-cols-[1fr_auto] lg:items-start lg:gap-16">
          <div className="lg:order-1">
            <div className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
              Risk oversight
            </div>
            <h2 className="mt-2 font-display text-3xl font-bold tracking-tight md:text-4xl">
              Model-Specific Controls. Persistent Market Risk.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              Each live engine operates under its own risk policy. Publication can be restricted or paused, but losses and execution differences remain possible. Internal control settings are not part of the public product description.
            </p>

            <div className="mt-8 grid gap-4 md:grid-cols-2">
              <HighlightCard
                icon={<Landmark className="h-5 w-5" />}
                title="Selective Publication"
                body="There is no obligation to publish on a schedule. A period without opportunities is an acceptable product state."
              />
              <HighlightCard
                icon={<ShieldCheck className="h-5 w-5" />}
                title="Execution Responsibility"
                body="You remain responsible for brokerage execution, position sizing and account limits. Published levels cannot guarantee a particular fill."
              />
            </div>
          </div>
          <div className="flex justify-center lg:order-2 lg:justify-end">
            <div className="relative">
              <div className="absolute -inset-3 rounded-2xl bg-gradient-gold opacity-20 blur-xl" aria-hidden />
              <div className="relative flex h-24 w-24 items-center justify-center rounded-2xl border border-gold/40 bg-gold-muted text-gold">
                <ShieldCheck className="h-12 w-12" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4b. Member workflow pipeline */}
      <section className="container mx-auto max-w-6xl px-4 py-20 md:py-24">
        <div className="rounded-2xl border border-gold/20 bg-slate-deep/40 p-6 md:p-10">
          <div className="mx-auto max-w-2xl text-center">
            <div className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
              Member workflow
            </div>
            <h2 className="mt-2 font-display text-3xl font-bold tracking-tight md:text-4xl">
              How to Read a Published Opportunity
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
              These steps describe the member experience, not the internal engine sequence.
            </p>
          </div>

          <div className="mt-10 flex flex-col items-stretch gap-4 lg:flex-row lg:items-center lg:gap-2">
            <PipelineStep
              step={1}
              title="Review Attribution"
              icon={<Activity className="h-5 w-5" />}
              body="Identify the engine and the displayed grade before reviewing its published plan."
            />
            <PipelineConnector />
            <PipelineStep
              step={2}
              title="Read the Plan"
              icon={<Droplets className="h-5 w-5" />}
              body="Eligible members review the published levels and apply their own account constraints."
            />
            <PipelineConnector />
            <PipelineStep
              step={3}
              title="Follow Updates"
              icon={<ShieldCheck className="h-5 w-5" />}
              body="Review trade status in the XAU Terminal™ and alerts available for your access."
            />
            <PipelineConnector />
            <PipelineStep
              step={4}
              title="Review Outcomes"
              icon={<CheckCircle2 className="h-5 w-5" />}
              body="Read the closed record, including losses. Broker execution may differ from recorded results."
              approved
            />
          </div>
        </div>
      </section>

      {/* 5. Radical Transparency */}
      <section className="border-y border-border/60 bg-slate-deep">
        <div className="container mx-auto max-w-6xl px-4 py-20 md:py-24">
          <div className="mx-auto max-w-2xl text-center">
            <div className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
              Radical Transparency
            </div>
            <h2 className="mt-2 font-display text-3xl font-bold tracking-tight md:text-4xl">
              Trading with Context, Not Guesswork
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
              Published information separates model attribution, trade status and recorded outcomes. Internal decision criteria are not disclosed.
            </p>
          </div>

          <div className="mx-auto mt-12 grid max-w-5xl gap-5 md:grid-cols-3">
            <BadgeCard
              tone="aligned"
              icon={<ShieldCheck className="h-3 w-3" />}
              label="Model"
              title="Engine Attribution"
              body="Each published opportunity identifies its source engine."
            />
            <BadgeCard
              tone="neutral"
              icon={<Activity className="h-3 w-3" />}
              label="Status"
              title="Trade Updates"
              body="Follow the current state of a published opportunity."
            />
            <BadgeCard
              tone="caution"
              icon={<AlertTriangle className="h-3 w-3" />}
              label="Record"
              title="Closed Results"
              body="Review outcomes separately from expectations."
            />
          </div>
        </div>
      </section>

      {/* 5b. Risk geometry */}
      <section className="container mx-auto max-w-6xl px-4 py-20 md:py-24">
        <div className="mx-auto max-w-2xl text-center">
          <div className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
            Access and responsibility
          </div>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight md:text-4xl">
            Published Levels. Independent Execution.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
            Live models retain separate operating policies. Member-facing levels do not disclose the methods used to construct them.
          </p>
        </div>
        <div className="mx-auto mt-10 grid max-w-4xl gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { k: "Trade Plan", v: "Levels", d: "Published levels are available according to membership." },
            { k: "Execution", v: "Your decision", d: "Orion AI does not place brokerage orders for you." },
            { k: "Account Risk", v: "Your limits", d: "Position size and funded-account compliance remain your responsibility." },
            { k: "Research", v: "Separate", d: "OEAS-01 remains lab-only, not a live member service." },
          ].map((r) => (
            <div key={r.k} className="rounded-xl border border-border/60 bg-card p-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {r.k}
              </div>
              <div className="mt-2 font-mono text-2xl font-bold text-gold">{r.v}</div>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{r.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 5c. Live tools */}
      <section className="border-y border-border/60 bg-slate-deep">
        <div className="container mx-auto max-w-6xl px-4 py-16">
          <div className="mx-auto max-w-2xl text-center">
            <div className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
              Public market context
            </div>
            <h2 className="mt-2 font-display text-2xl font-bold tracking-tight md:text-3xl">
              Explore Gold Market Reports
            </h2>
          </div>
          <div className="mx-auto mt-8 grid max-w-4xl gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {(
              [
                { to: "/gold-cot-report", label: "Gold COT Report" },
                { to: "/gold-inflation-cpi", label: "Gold and CPI" },
                { to: "/gold-nfp-employment", label: "Gold and Payrolls" },
                { to: "/gold-seasonality", label: "Gold Seasonality" },
              ] as const
            ).map((t) => (
              <Link
                key={t.to}
                to={t.to}
                className="flex items-center justify-between rounded-lg border border-border/60 bg-card px-4 py-3 font-mono text-[11px] uppercase tracking-[0.16em] transition hover:border-gold/50 hover:text-gold"
              >
                {t.label} <ChevronRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 5d. FAQ */}
      <section className="container mx-auto max-w-3xl px-4 py-20">
        <h2 className="font-display text-3xl font-bold tracking-tight">Frequently Asked Questions</h2>
        <Accordion type="single" collapsible className="mt-6">
          {ORION_FAQ.map((f, i) => (
            <AccordionItem key={i} value={`q-${i}`}>
              <AccordionTrigger className="text-left font-semibold">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* 6. Outro */}
      <section className="container mx-auto max-w-3xl px-4 py-20 text-center md:py-28">
        <h2 className="font-display text-3xl font-bold leading-tight tracking-tight md:text-5xl">
          Quality Over Quantity. <span className="text-gradient-gold">Always.</span>
        </h2>
        <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
          Not every candle deserves a signal. XAU Pro Academy and the ORION engine are designed
          to reduce exposure during high risk conditions. Fewer signals. Defined risk. Every
          decision logged.
        </p>
        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button asChild variant="gold" size="xl">
            <Link to="/signals">
              Open the Signals Desk <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button asChild variant="outline" size="xl">
            <Link to="/track-record">View the Performance Record</Link>
          </Button>
          <Button asChild variant="ghost" size="xl">
            <Link to="/orion/how-it-works">How Orion thinks</Link>
          </Button>
        </div>
        <p className="mt-6 text-[11px] uppercase tracking-wider text-muted-foreground">
          For educational purposes only. Not financial advice.
        </p>
      </section>
    </Layout>
  );
}

function FeatureCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-xl border border-border/60 bg-card p-5 transition hover:border-gold/40">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gold-muted text-gold">
        {icon}
      </div>
      <h3 className="mt-4 font-display text-base font-semibold leading-snug">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}

function HighlightCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-gold/20 bg-card p-6 transition hover:border-gold/50">
      <div
        className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full opacity-20 blur-2xl"
        style={{ background: "radial-gradient(closest-side, hsl(var(--gold) / 0.6), transparent)" }}
      />
      <div className="relative">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gold-muted text-gold">
          {icon}
        </div>
        <h3 className="mt-4 font-display text-lg font-semibold">{title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
      </div>
    </div>
  );
}

function BadgeCard({
  tone,
  icon,
  label,
  title,
  body,
}: {
  tone: "aligned" | "neutral" | "caution";
  icon: React.ReactNode;
  label: string;
  title: string;
  body: string;
}) {
  const chipClasses =
    tone === "aligned"
      ? "border-emerald/40 bg-emerald-muted text-emerald"
      : tone === "caution"
        ? "border-gold/40 bg-gold-muted/30 text-gold"
        : "border-border/60 bg-slate-deep/60 text-muted-foreground";
  const borderClass =
    tone === "aligned"
      ? "border-emerald/30 hover:border-emerald/60"
      : tone === "caution"
        ? "border-gold/30 hover:border-gold/60"
        : "border-border/60 hover:border-border";
  return (
    <div className={`rounded-xl border bg-card p-6 transition ${borderClass}`}>
      <span
        className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${chipClasses}`}
      >
        {icon}
        {label}
      </span>
      <h3 className="mt-4 font-display text-lg font-semibold">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}

function PipelineStep({
  step,
  title,
  icon,
  body,
  approved,
}: {
  step: number;
  title: string;
  icon: React.ReactNode;
  body: string;
  approved?: boolean;
}) {
  const tileClass = approved
    ? "border border-gold/50 bg-gradient-gold text-gold-foreground shadow-lg shadow-gold/20"
    : "border border-gold/20 bg-gold-muted text-gold";
  return (
    <div
      className={`relative flex-1 rounded-xl border bg-card p-5 transition hover:border-gold/40 ${
        approved ? "border-gold/40" : "border-border/60"
      }`}
    >
      <div className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
        Step {String(step).padStart(2, "0")}
      </div>
      <div className={`mt-3 flex h-10 w-10 items-center justify-center rounded-lg ${tileClass}`}>
        {icon}
      </div>
      <h3 className="mt-4 font-display text-base font-semibold leading-snug">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}

function PipelineConnector() {
  return (
    <div
      className="flex shrink-0 items-center justify-center text-gold/60 lg:w-8"
      aria-hidden
    >
      {/* Mobile: vertical */}
      <div className="flex flex-col items-center lg:hidden">
        <div className="h-4 w-px bg-gradient-to-b from-gold/40 via-gold/20 to-transparent" />
        <ChevronDown className="h-4 w-4" />
      </div>
      {/* Desktop: horizontal */}
      <div className="hidden w-full items-center lg:flex">
        <div className="h-px flex-1 bg-gradient-to-r from-gold/10 via-gold/30 to-gold/10" />
        <ChevronRight className="h-4 w-4" />
      </div>
    </div>
  );
}
